// @flow
import React, {useState} from 'react';
import Debug from 'debug';
import {FormattedMessage} from 'react-intl';
import {makeStyles} from '@material-ui/core/styles';
import Typography from '@material-ui/core/Typography';
import Button from '@material-ui/core/Button';
import TextField from '@material-ui/core/TextField';
import SnackbarContent from '@material-ui/core/SnackbarContent';
import firebase from '../../services/firebase';
import Spinner from '../ui/Spinner';
import mainSrc from '../../assets/images/main.svg';


const debug = Debug('amms:modules:login');

const useStyles = makeStyles(theme => ({
    container: {
        display: 'flex',
        justifyContent: 'center',
        paddingLeft: 32,
        paddingRight: 32,
        backgroundImage: 'linear-gradient(45deg, #40e2cf 0%, #8668f4 100%)',
        minHeight: '100vh',
    },
    panel: {
        width: 420,
        maxWidth: 420
    },
    item: {
        display: 'flex',
        flexGrow: 1,
        flexBasis: 0,
        justifyContent: 'center',
        alignItems: 'center',
        maxWidth: 480
    },
    image: {
        width: 200,
        marginBottom: -260
    },
    label: {
        display: 'block',
        marginTop: theme.spacing(2),
        marginBottom: theme.spacing(1),
        fontSize: theme.typography.h6.fontSize
    },
    input: {
        marginBottom: theme.spacing(2),
        '& .MuiOutlinedInput-input': {
            backgroundColor: theme.palette.common.white,
            borderRadius: 4,
            boxShadow:
                'inset 0px 2px 4px -1px rgba(0,0,0,0.2),' +
                'inset 0px 4px 5px 0px rgba(0,0,0,0.14),' +
                'inset 0px 1px 10px 0px rgba(0,0,0,0.12)'
        }
    },
    button: {
        marginTop: theme.spacing(2),
        minWidth: '60%',
        minHeight: 56,
        fontSize: theme.typography.h6.fontSize
    },
    error: {
        backgroundColor: theme.palette.error.dark,
        marginTop: theme.spacing(2),
        marginBottom: theme.spacing(2)
    }
}));

export type Props = $ReadOnly<{
    isLoading: boolean,

    loggingIn: (finished?: boolean) => void
}>;

export default function Login({isLoading, loggingIn}: Props) {
    const classes = useStyles();
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [error, setError] = useState(false);

    const handleChange = (setter) => (e) => {
        setter(e.target.value);
        setError(null);
    };

    const onLoginError = () => {
        setError(true);
        loggingIn(true);
    };

    const onSubmit = (e) => {
        e.preventDefault();

        loggingIn();

        firebase.auth().signInWithEmailAndPassword(email, password)
            .then(() => {
                firebase.auth().currentUser.getIdTokenResult()
                    .then((idTokenResult) => {
                        // Confirm the user is an Admin.
                        if (!idTokenResult.claims.admin) {
                            firebase.auth().signOut();

                            console.error(new Error('User does not have an administrator account!'));
                            debug('User is not admin');
                            onLoginError(e);
                        } else {
                            loggingIn(true);
                        }
                    })
                    .catch(e => {
                        debug('Token result error', e);
                        onLoginError(e);
                    });
            })
            .catch(function (e) {
                debug('User login error', e);
                onLoginError(e);
            });
    };

    return (
        <div className={classes.container}>
            <div className={classes.item}>
                <div className={classes.panel}>
                    <Typography variant="h4" component="h1" style={{ fontWeight: 500 }}>
                        <FormattedMessage
                            id="login.heading"
                            defaultMessage="AMMS Console"
                        />
                    </Typography>
                    <form onSubmit={onSubmit}>
                        <Spinner isLoading={isLoading}>
                            <label className={classes.label}>Email</label>
                            <TextField
                                classes={{ root: classes.input}}
                                id="email"
                                // label="Email"
                                margin="none"
                                variant="outlined"
                                fullWidth
                                InputLabelProps={{
                                    shrink: true,
                                }}
                                value={email}
                                onChange={handleChange(setEmail)}
                            />

                            <label className={classes.label}>Password</label>
                            <TextField
                                classes={{ root: classes.input}}
                                id="password"
                                // label="Password"
                                type="password"
                                margin="none"
                                variant="outlined"
                                fullWidth
                                InputLabelProps={{
                                    shrink: true,
                                }}
                                value={password}
                                onChange={handleChange(setPassword)}
                            />
                        </Spinner>

                        {error &&
                        <SnackbarContent
                            className={classes.error}
                            message={
                                <FormattedMessage
                                    id="login.error"
                                    defaultMessage="Login unsuccessful"
                                />
                            }
                        />
                        }

                        <Button
                            className={classes.button}
                            type="submit"
                            variant="contained"
                            color="primary"
                        >
                            <FormattedMessage
                                id="login.loginCtaButton"
                                defaultMessage="Sign in"
                            />
                        </Button>
                    </form>
                </div>
            </div>
            <div className={classes.item} style={{ overflow: 'hidden' }}>
                <img src={mainSrc} className={classes.image} />
            </div>
        </div>
    );
}